import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { fetchTheme, saveThemeSettings, saveCategoryColor, DEFAULT_THEME, THEME_PRESETS } from "../settingsApi";
import ModelUploadField from "./ModelUploadField";

const IDENTITY_FIELDS = [
  { key: "app_name", label: "Nama Aplikasi", type: "text" },
  { key: "app_subtitle", label: "Subtitle Aplikasi", type: "text" },
  { key: "footer_text", label: "Teks Footer", type: "text", hint: "gunakan {year} untuk tahun otomatis" },
];

export default function AppSettings() {
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { type: "success"|"error", message }

  const loadData = async () => {
    setLoading(true);
    const [{ theme: fetchedTheme }, { data: cats }] = await Promise.all([
      fetchTheme(),
      supabase.from("categories").select("*").order("sort_order"),
    ]);
    setThemeState(fetchedTheme);
    setCategories(cats || []);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateThemeField = (key, value) => setThemeState((prev) => ({ ...prev, [key]: value }));

  // Pilih preset = ganti theme_preset + semua warna sekaligus dari
  // THEME_PRESETS, supaya user tidak perlu atur satu-satu.
  const selectPreset = (presetKey) => {
    setThemeState((prev) => ({ ...prev, ...THEME_PRESETS[presetKey], theme_preset: presetKey }));
  };

  const updateCategoryColor = (categoryId, color) =>
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, coverage_color: color } : c)));

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);

    const { error: themeErr } = await saveThemeSettings(theme);

    const categoryResults = await Promise.all(
      categories.map((c) => saveCategoryColor(c.id, c.coverage_color))
    );
    const categoryErr = categoryResults.find((r) => r.error)?.error;

    setSaving(false);

    if (themeErr || categoryErr) {
      setNotice({ type: "error", message: (themeErr || categoryErr).message });
      return;
    }
    setNotice({ type: "success", message: "Pengaturan berhasil disimpan. Refresh halaman RoomDesigner untuk melihat perubahan." });
  };

  if (loading) {
    return <div style={{ padding: 28, color: "#94a3b8", fontSize: 13 }}>Memuat pengaturan...</div>;
  }

  return (
    <div style={{ padding: "24px 28px", maxWidth: 720 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
        Pengaturan Tampilan
      </div>
      <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20 }}>
        Mengatur tema warna dan identitas aplikasi RoomDesigner secara langsung dari sini.
      </div>

      {notice && (
        <div style={{
          background: notice.type === "success" ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${notice.type === "success" ? "#bbf7d0" : "#fecaca"}`,
          color: notice.type === "success" ? "#166534" : "#dc2626",
          fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16,
        }}>
          {notice.message}
        </div>
      )}

      {/* ── Pilihan tema preset ── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Tema Tampilan</div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
          Pilih salah satu — seluruh warna navbar, sidebar, dan footer otomatis menyesuaikan.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {Object.entries(THEME_PRESETS).map(([key, preset]) => {
            const active = theme.theme_preset === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => selectPreset(key)}
                style={{
                  border: `2px solid ${active ? "#3b82f6" : "#e2e8f0"}`,
                  borderRadius: 12, padding: 0, cursor: "pointer",
                  background: "#fff", overflow: "hidden", textAlign: "left",
                  transition: "border-color 0.15s",
                }}
              >
                {/* Preview mini tampilan navbar+sidebar sesuai preset */}
                <div style={{ background: preset.theme_bg, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{
                    background: preset.theme_surface, borderRadius: 6, padding: "8px 10px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <div style={{ width: 40, height: 8, borderRadius: 4, background: preset.theme_text_muted }} />
                    <div style={{
                      width: 36, height: 16, borderRadius: 4, background: preset.theme_accent,
                    }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1, background: preset.theme_surface, borderRadius: 6, height: 48 }} />
                    <div style={{ flex: 2, background: preset.theme_surface, borderRadius: 6, height: 48, opacity: 0.6 }} />
                  </div>
                </div>
                <div style={{
                  padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between",
                  borderTop: "1px solid #f1f5f9",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{preset.label}</span>
                  {active && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: "#fff", background: "#3b82f6",
                      borderRadius: 999, padding: "2px 8px",
                    }}>
                      Aktif
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Identitas aplikasi ── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Identitas Aplikasi</div>

        {IDENTITY_FIELDS.map((field) => (
          <div key={field.key} style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
              {field.label}
            </label>
            <input
              type="text"
              value={theme[field.key] || ""}
              onChange={(e) => updateThemeField(field.key, e.target.value)}
              style={inputStyle}
            />
            {field.hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{field.hint}</div>}
          </div>
        ))}
      </div>

      {/* ── Warna kategori produk ── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Warna Kategori Produk</div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
          Menentukan warna untuk setiap kategori produk — dipakai di coverage zone 3D, indikator warna pada footer, dan titik warna kategori di sidebar katalog & daftar perangkat yang sudah ditempatkan.
        </div>

        {categories.map((cat) => (
          <div key={cat.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{cat.label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="color"
                value={cat.coverage_color}
                onChange={(e) => updateCategoryColor(cat.id, e.target.value)}
                style={{ width: 36, height: 30, borderRadius: 6, border: "1px solid #cbd5e1", padding: 2, cursor: "pointer" }}
              />
              <span style={{ fontSize: 12, fontFamily: "monospace", color: "#64748b", minWidth: 64 }}>{cat.coverage_color}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Model 3D Furniture (Meja & Kursi) ── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Model 3D Furniture (Meja & Kursi)</div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
          Upload file .glb untuk mengganti tampilan meja dan kursi bawaan di RoomDesigner. Berlaku untuk SEMUA meja & kursi di sistem (bukan per-produk). Kosongkan untuk kembali memakai geometri bawaan (meja standar + kursi kantor prosedural).
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <FurnitureModelField
            label="Model Meja"
            hint="di-scale otomatis mengikuti ukuran meja yang diatur di RoomDesigner (lebar × tinggi × dalam)"
            url={theme.table_model_url}
            scale={theme.table_model_scale}
            rotationY={theme.table_model_rotation_y}
            onUrlChange={(url) => updateThemeField("table_model_url", url || "")}
            onScaleChange={(v) => updateThemeField("table_model_scale", v)}
            onRotationChange={(v) => updateThemeField("table_model_rotation_y", v)}
            productIdPrefix="furniture-table"
          />
          <FurnitureModelField
            label="Model Kursi"
            hint="di-scale ke ukuran kursi standar (~46×100×55cm); dipakai berulang untuk semua posisi kursi di sekeliling meja"
            url={theme.chair_model_url}
            scale={theme.chair_model_scale}
            rotationY={theme.chair_model_rotation_y}
            onUrlChange={(url) => updateThemeField("chair_model_url", url || "")}
            onScaleChange={(v) => updateThemeField("chair_model_scale", v)}
            onRotationChange={(v) => updateThemeField("chair_model_rotation_y", v)}
            productIdPrefix="furniture-chair"
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: "10px 24px", borderRadius: 8, border: "none",
          background: saving ? "#93c5fd" : "#3b82f6", color: "#fff",
          fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer",
        }}
      >
        {saving ? "Menyimpan..." : "Simpan Pengaturan"}
      </button>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1",
  fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

// Field upload model 3D furniture (meja/kursi) — bungkus ModelUploadField
// yang sama dipakai produk, ditambah kontrol skala & rotasi koreksi.
function FurnitureModelField({ label, hint, url, scale, rotationY, onUrlChange, onScaleChange, onRotationChange, productIdPrefix }) {
  const rotVal = Number(rotationY) || 0;
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
        {label}
      </label>

      <ModelUploadField
        value={url || null}
        onChange={onUrlChange}
        productId={productIdPrefix}
        rotationY={rotVal}
      />

      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, marginBottom: 12 }}>{hint}</div>}

      {url && (
        <>
          <div style={{ marginTop: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              Skala Model (faktor koreksi manual)
            </div>
            <input
              type="number" step="0.01" min="0.01"
              value={Number(scale) || 1}
              onChange={(e) => onScaleChange(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              Rotasi Koreksi Arah Model
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="range" min={-180} max={180} step={1}
                value={rotVal}
                onChange={(e) => onRotationChange(e.target.value)}
                style={{ flex: 1, accentColor: "#3b82f6" }}
              />
              <div style={{
                minWidth: 52, textAlign: "center", padding: "4px 8px", borderRadius: 6,
                background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12,
                fontWeight: 700, color: "#3b82f6", fontFamily: "monospace",
              }}>
                {rotVal > 0 ? `+${rotVal}` : rotVal}°
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}